import { type MouseEvent, useCallback, useEffect, useRef } from "react";
import type { RowData } from "@tanstack/react-table";
import { getCell } from "../../editing/cellAccess";
import { startFillDrag } from "../../editing/fillDrag";
import type { GridController } from "../../focus/gridController";
import {
    CELL_CONTROL_SELECTOR,
    isInsideMatching,
} from "../../cells/cellControls";
import {
    getCellAddressFromTarget,
    getFocusedAddress,
    isSameCellAddress,
} from "../../focus/navigation";
import type { DataGridInstance } from "../../types";

/** Content that keeps its own mouse behavior inside a range-selection grid. */
const OWN_MOUSE_SELECTOR = [CELL_CONTROL_SELECTOR, "[data-dt-editor]"].join(
    ", ",
);

const GESTURE_ATTRIBUTE = "data-range-gesture";

/** Marks a drag that reshapes the range, which hides the range action, until the mouse is released. */
const markRangeGesture = (
    viewport: HTMLElement | null,
    signal: AbortSignal | undefined,
) => {
    if (!viewport || viewport.hasAttribute(GESTURE_ATTRIBUTE)) {
        return;
    }
    viewport.setAttribute(GESTURE_ATTRIBUTE, "");
    document.addEventListener(
        "mouseup",
        () => viewport.removeAttribute(GESTURE_ATTRIBUTE),
        { once: true, signal },
    );
};

interface UseRangeMouseInput<TData extends RowData> {
    instance: DataGridInstance<TData>;
    controller: GridController;
}

/**
 * Range selection by mouse, delegated on the grid element: mousedown starts a
 * range (Shift extends the active one), dragging across cells extends it, and
 * the corner handle (`editing.fillHandle`) starts a fill drag. Shift+click
 * keeps DOM focus on the active cell, which stays the range's anchor.
 */
export const useRangeMouse = <TData extends RowData>({
    instance,
    controller,
}: UseRangeMouseInput<TData>) => {
    const { table, hasRangeSelection } = instance;
    const pressedRef = useRef<{ rowId: string; columnId: string } | null>(null);
    const teardownRef = useRef<AbortController | null>(null);
    useEffect(() => {
        const teardown = new AbortController();
        teardownRef.current = teardown;
        return () => teardown.abort();
    }, []);

    const onMouseDown = useCallback(
        (event: MouseEvent<HTMLDivElement>) => {
            if (!hasRangeSelection || event.button !== 0) {
                return;
            }
            const { target } = event;
            if (
                instance.hasFillHandle &&
                target instanceof Element &&
                target.closest("[data-dt-fill-handle]")
            ) {
                event.preventDefault();
                const teardown = teardownRef.current;
                markRangeGesture(controller.viewport, teardown?.signal);
                if (teardown) {
                    startFillDrag({
                        context: { instance, controller },
                        signal: teardown.signal,
                    });
                }
                return;
            }
            if (
                !(target instanceof Element) ||
                isInsideMatching({
                    target,
                    container: event.currentTarget,
                    selector: OWN_MOUSE_SELECTOR,
                })
            ) {
                return;
            }
            const address = getCellAddressFromTarget(target);
            const cell = address ? getCell(table, address) : undefined;
            if (!address || !cell?.getCanSelect()) {
                return;
            }
            pressedRef.current = address;
            document.addEventListener(
                "mouseup",
                () => {
                    pressedRef.current = null;
                },
                { once: true, signal: teardownRef.current?.signal },
            );
            const ranges = table.atoms.cellSelection.get();
            const active = ranges.at(-1);
            if (event.shiftKey && active && ranges.length > 1) {
                table.setCellSelection([active]);
            }
            cell.getSelectionStartHandler()(event);
            const isEditorOpen = instance.atoms.activeEditor.get() !== null;
            if (event.shiftKey && !isEditorOpen) {
                event.preventDefault();
                const anchor = getFocusedAddress(
                    table.atoms.cellSelection.get(),
                );
                if (anchor) {
                    controller.focusTarget(anchor);
                }
            }
        },
        [controller, hasRangeSelection, instance, table],
    );

    const onMouseOver = useCallback(
        (event: MouseEvent<HTMLDivElement>) => {
            if (!hasRangeSelection || event.buttons !== 1) {
                return;
            }
            const address = getCellAddressFromTarget(event.target);
            const cell = address ? getCell(table, address) : undefined;
            const pressed = pressedRef.current;
            if (address && pressed && !isSameCellAddress(address, pressed)) {
                markRangeGesture(
                    controller.viewport,
                    teardownRef.current?.signal,
                );
            }
            cell?.getSelectionExtendHandler()(event);
        },
        [controller, hasRangeSelection, table],
    );

    return { onMouseDown, onMouseOver };
};
